// Masking pipeline (PRIV). Applied to console args and network bodies before
// anything leaves the page. Heuristics catch the obvious PII; tenants tighten
// with maskTextSelector / beforeSend.

import type { ObsrviqEvent } from '@logclip/types';

const EMAIL = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;
const CC = /\b(?:\d[ -]*?){13,19}\b/g; // candidate card numbers
const SSN = /\b\d{3}-\d{2}-\d{4}\b/g;
const LONG_DIGITS = /\b\d{9,}\b/g; // phone-ish / account-ish

// Keys whose values we always redact, regardless of content. Two tiers so we
// catch real secrets without over-redacting innocent fields:
//   STRONG — long/unambiguous tokens, safe to match as a substring of the key
//            name (e.g. "accessToken", "x-api-key", "clientSecret").
//   WEAK   — short/ambiguous tokens that we only honour when they stand alone as
//            a key SEGMENT, so "shipping"(pin) / "discard"(card) / "author"(auth)
//            aren't caught but "pin" / "cardNumber" / "authToken" are.
const SENSITIVE_STRONG =
  /(password|passwd|passphrase|secret|token|authoriz|apikey|api[_-]key|creditcard|cardnumber|cardholder|cookie|cvv|cvc)/i;
const SENSITIVE_WEAK = new Set(['pass', 'pwd', 'auth', 'card', 'pin', 'otp', 'ssn', 'cvv', 'cvc']);

/** Split a key name into lowercase word segments on delimiters AND camelCase
 *  boundaries: "accessToken" → [access, token], "api_key" → [api, key]. */
function keySegments(key: string): string[] {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map((s) => s.toLowerCase());
}

/**
 * Strip identifying material out of a URL before it leaves the page.
 *
 * Query strings routinely carry magic-link tokens, password-reset tokens and
 * `?email=` parameters, so the search is always replaced wholesale rather than
 * filtered — an allowlist of "safe" params is a list you will forget to update.
 *
 * The fragment is treated the same way ONLY when it looks like it carries
 * values (`#access_token=…`, which is exactly how the OAuth implicit flow
 * returns a token). A hash-router route (`#/checkout`) or a plain anchor
 * (`#pricing`) carries no secret and is real navigation information, so both
 * survive.
 *
 * The marker is ASCII: an ellipsis round-trips through URL.toString() as
 * %E2%80%A6, and these strings are rendered back to a human in the session
 * list.
 *
 * Lives here rather than beside its callers because it was previously
 * copy-pasted into network.ts and websocket.ts, which is how the page URL came
 * to be missed entirely: two copies existed, and nothing applied either of them
 * to `location.href`.
 */
export function redactUrl(raw: string): string {
  try {
    const u = new URL(raw, location.href);
    if (u.search) u.search = '?redacted';
    if (u.hash.includes('=')) u.hash = '#redacted';
    return u.toString();
  } catch {
    // Not parseable as a URL — fall back to cutting at the first delimiter.
    return raw.split(/[?#]/)[0] ?? raw;
  }
}

export function maskString(input: string): string {
  return input
    .replace(EMAIL, '«email»')
    .replace(CC, (m) => (luhnish(m) ? '«card»' : m))
    .replace(SSN, '«ssn»')
    .replace(LONG_DIGITS, '«number»');
}

function luhnish(s: string): boolean {
  const digits = s.replace(/\D/g, '');
  return digits.length >= 13 && digits.length <= 19;
}

// Customer-defined keys to mask (case-insensitive exact match), delivered via the
// per-site config (GET /v1/config) and/or the init `maskKeys` option. Additive to
// the built-in list above — you can only add protections, never remove them.
let customKeys = new Set<string>();
export function setCustomMaskKeys(keys: string[]): void {
  customKeys = new Set(keys.map((k) => String(k).trim().toLowerCase()).filter(Boolean));
}

// Whether the per-site mask config (GET /v1/config) has settled. Until it does,
// remote-only custom keys aren't known yet, so the transport holds non-unload
// sends (below) to avoid leaking a custom-keyed value in the brief init window.
let maskConfigSettled = false;
export function markMaskConfigReady(): void {
  maskConfigSettled = true;
}
export function isMaskConfigPending(): boolean {
  return !maskConfigSettled;
}
export function hasCustomMaskKeys(): boolean {
  return customKeys.size > 0;
}
function isCustomKey(key: string): boolean {
  return customKeys.has(key.toLowerCase());
}

/** Key-based sensitivity test: built-in STRONG/WEAK tiers PLUS any customer-
 *  configured keys (exact, case-insensitive). Boundary-aware so innocent fields
 *  like "shipping" / "discard" / "author" aren't redacted. */
export function isSensitiveKeyName(key: string): boolean {
  if (isCustomKey(key)) return true;
  if (SENSITIVE_STRONG.test(key)) return true;
  return keySegments(key).some((seg) => SENSITIVE_WEAK.has(seg));
}

/** Redact sensitive-key values in a parsed object (built-in list + custom keys).
 *  The send-time safety net that guarantees a `{"password": "…"}` style body can
 *  never leave the page unmasked, even for non-DOM network/WS payloads (whose
 *  capture-time masking is PII-pattern only, not key-aware). */
function redactSensitiveInValue(value: unknown, depth = 0): unknown {
  if (depth > 24 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((v) => redactSensitiveInValue(v, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = isSensitiveKeyName(k) ? '«redacted»' : redactSensitiveInValue(v, depth + 1);
  }
  return out;
}

/** String-level key redaction for payloads that don't parse as JSON — non-JSON
 *  bodies (form-urlencoded / query strings) AND truncated JSON, where the
 *  structured path bails. Captures each `"key": value` (JSON-ish, tolerant of a
 *  cut-off tail) and `key=value` (form / query) key name and redacts the value
 *  when the key is sensitive. */
function redactSensitiveInRawText(text: string): string {
  let out = text.replace(
    /"([A-Za-z0-9_.\-]+)"\s*:\s*("(?:[^"\\]|\\.)*"|[^,}\]\s]+)/g,
    (m, key: string) => (isSensitiveKeyName(key) ? '"' + key + '": "«redacted»"' : m),
  );
  out = out.replace(
    /(^|[?&])([A-Za-z0-9_.\-[\]]+)=([^&\s]*)/g,
    (m, pre: string, key: string) => (isSensitiveKeyName(key) ? pre + key + '=«redacted»' : m),
  );
  return out;
}

/** Redact sensitive-key values in a stored body/arg string. Tries structured
 *  JSON first; falls back to a string scan for non-JSON or truncated payloads so a
 *  sensitive key can't slip through in `email=a&password=b` or a cut-off body. */
function redactSensitiveInText(text: string | undefined): string | undefined {
  if (!text) return text;
  const trimmed = text.trim();
  if (trimmed[0] === '{' || trimmed[0] === '[') {
    try {
      return JSON.stringify(redactSensitiveInValue(JSON.parse(trimmed)));
    } catch {
      /* truncated / invalid JSON — fall through to the string scan */
    }
  }
  return redactSensitiveInRawText(text);
}

/** Send-time pass: mask sensitive keys (built-in list + custom keys) across a
 *  batch's bodies / frames / console args / custom props (mutates in place).
 *  Always runs — the built-in list applies with no per-site config needed. */
export function redactBatchSensitiveKeys(events: ObsrviqEvent[]): void {
  for (const e of events) {
    if (e.type === 'network' || e.type === 'ws') {
      const anyE = e as unknown as {
        requestBody?: { preview?: string };
        responseBody?: { preview?: string };
        body?: { preview?: string };
      };
      if (anyE.requestBody?.preview) anyE.requestBody.preview = redactSensitiveInText(anyE.requestBody.preview);
      if (anyE.responseBody?.preview) anyE.responseBody.preview = redactSensitiveInText(anyE.responseBody.preview);
      if (anyE.body?.preview) anyE.body.preview = redactSensitiveInText(anyE.body.preview);
    } else if (e.type === 'custom') {
      const c = e as unknown as { props?: Record<string, unknown> };
      if (c.props) c.props = redactSensitiveInValue(c.props) as Record<string, unknown>;
    } else if (e.type === 'console') {
      const args = (e as unknown as { args?: Array<{ kind: string; json?: string }> }).args;
      if (args) for (const a of args) if (a.kind === 'object' && a.json) a.json = redactSensitiveInText(a.json);
    }
  }
}

/** Recursively mask an object's string values and sensitive keys. */
export function maskValue(value: unknown, depth = 0): unknown {
  if (depth > 6) return '«depth»';
  if (typeof value === 'string') return maskString(value);
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((v) => maskValue(v, depth + 1));
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = isSensitiveKeyName(k) ? '«redacted»' : maskValue(v, depth + 1);
  }
  return out;
}
